/**
 * Locale negotiation, message bundles and RTL (§15.4). Bundles are flat key → ICU message (plural/select
 * supported by the kit's `formatMessage`). English is in the main bundle; other locales load on demand.
 * Participant-safe status copy keys mirror `PARTICIPANT_STATUS_COPY` as `status.<condition>`.
 */
import { useEffect, useState } from 'react';
import type { Messages } from '@windrop/ui';
import en from './en.json';

export const DEFAULT_LOCALE = 'en';
/** Locales with a bundle in this app. Lottery `locales` constrain what is offered. */
export const BUNDLED_LOCALES = ['en', 'fr', 'ar'] as const;
export type BundledLocale = (typeof BUNDLED_LOCALES)[number];

const RTL_LANGS = new Set(['ar', 'he', 'fa', 'ur', 'ps', 'sd', 'ug', 'yi', 'dv']);

export const baseLang = (tag: string): string => tag.toLowerCase().split(/[-_]/)[0] ?? tag.toLowerCase();

export function isRtl(locale: string): boolean {
  return RTL_LANGS.has(baseLang(locale));
}

export function textDirection(locale: string): 'rtl' | 'ltr' {
  return isRtl(locale) ? 'rtl' : 'ltr';
}

/**
 * Picks the locale to render: an explicit override (`?lang=` / preference) if the lottery offers it, else the
 * first browser language the lottery offers, else the lottery's default. Matching is on the base language so
 * `fr-CA` chooses the lottery's `fr`; the returned tag is the lottery's tag (what the content was written in).
 */
export function negotiateLocale(
  lotteryLocales: readonly string[],
  preferred: readonly string[] = typeof navigator !== 'undefined'
    ? (navigator.languages ?? [navigator.language])
    : [],
  override?: string | null,
): string {
  const offered = lotteryLocales.length ? lotteryLocales : [DEFAULT_LOCALE];
  const find = (tag: string | null | undefined) =>
    tag
      ? (offered.find((l) => l.toLowerCase() === tag.toLowerCase()) ??
        offered.find((l) => baseLang(l) === baseLang(tag)))
      : undefined;
  const fromOverride = find(override);
  if (fromOverride) return fromOverride;
  for (const p of preferred) {
    const m = find(p);
    if (m) return m;
  }
  return offered[0] ?? DEFAULT_LOCALE;
}

const cache = new Map<string, Messages>([[DEFAULT_LOCALE, en as Messages]]);

/** Loads the bundle for a locale (base language), falling back to English keys for anything missing. */
export async function loadMessages(locale: string): Promise<Messages> {
  const lang = baseLang(locale);
  const hit = cache.get(lang);
  if (hit) return hit;
  let extra: Messages = {};
  try {
    if (lang === 'fr') extra = (await import('./fr.json')).default as Messages;
    else if (lang === 'ar') extra = (await import('./ar.json')).default as Messages;
  } catch {
    extra = {};
  }
  const merged: Messages = { ...(en as Messages), ...extra };
  cache.set(lang, merged);
  return merged;
}

export function getMessagesSync(locale: string): Messages {
  return cache.get(baseLang(locale)) ?? (en as Messages);
}

/** Sets `lang` and `dir` on `<html>` (§15.4 RTL). */
export function applyDocumentLocale(
  locale: string,
  root: HTMLElement | null = typeof document !== 'undefined' ? document.documentElement : null,
): void {
  if (!root) return;
  root.setAttribute('lang', locale);
  root.setAttribute('dir', textDirection(locale));
}

/** React: messages for a locale; English until the bundle resolves, then re-renders. */
export function useMessages(locale: string): { messages: Messages; ready: boolean } {
  const [state, setState] = useState<{ locale: string; messages: Messages; ready: boolean }>(() => ({
    locale,
    messages: getMessagesSync(locale),
    ready: cache.has(baseLang(locale)),
  }));
  useEffect(() => {
    let cancelled = false;
    if (cache.has(baseLang(locale))) {
      setState({ locale, messages: getMessagesSync(locale), ready: true });
      return;
    }
    void loadMessages(locale).then((messages) => {
      if (!cancelled) setState({ locale, messages, ready: true });
    });
    return () => {
      cancelled = true;
    };
  }, [locale]);
  return { messages: state.messages, ready: state.ready && state.locale === locale };
}

/** Human-readable list in the locale ("Nigeria, Ghana and Kenya"). */
export function formatList(locale: string, items: readonly string[]): string {
  try {
    return new Intl.ListFormat(locale, { style: 'long', type: 'conjunction' }).format(items);
  } catch {
    return items.join(', ');
  }
}

/** Country code → localized display name. */
export function countryName(locale: string, code: string): string {
  try {
    return new Intl.DisplayNames([locale], { type: 'region' }).of(code) ?? code;
  } catch {
    return code;
  }
}

/** Locale tag → its own name ("Français"). */
export function localeName(locale: string): string {
  try {
    const name = new Intl.DisplayNames([locale], { type: 'language' }).of(locale) ?? locale;
    return name.charAt(0).toUpperCase() + name.slice(1);
  } catch {
    return locale;
  }
}
